import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Task, TaskAPI } from 'api/tasks';
import { ProgressBar } from 'components/molecules/progress-bar';
import { CreatedBy } from 'components/organisms/task-pane/task-detail/task-fields/default-task-fields/created-by';
import { EffortDurationField } from 'components/organisms/task-pane/task-detail/task-fields/default-task-fields/effort-as-duration';
import { NumberField } from 'components/organisms/tasks/fields';
import {
  PRIORITY_FIELD_NAME,
  useEffortField,
  usePriorityField,
} from 'data/fields/hooks';
import { useGetUserByUserId } from 'hooks/use-idb';
import {
  Wrapper as CpTDFieldSectionWrapper,
  FieldSectionTitle as CpTDFieldSectionTitle,
  FieldsWrapper as CpTDFieldsWrapper,
} from '../field-section';
import { CpTaskDrawerReadonlyField } from '../fields/cp-task-drawer-field';
import { CpTdSingleSelectField } from '../fields/cp-td-task-field-editors';

const StyledEffortDurationField = styled(EffortDurationField)`
  padding-left: 0;
`;

const PaddingLeft = styled.div`
  padding-left: var(--cp-spacing-05);
  display: flex;
  align-items: center;
  justify-content: start;
`;

const FullWidthSpan = styled.span`
  width: 100%;

  .ozone-select-container {
    width: 100%;
  }
`;

export const CpTaskDrawerDefaultFieldsSection: React.FC<{
  task: Task;
  refetchTask?: () => void;
}> = ({ task, refetchTask }) => {
  const [effortValue, setEffortValue] = useState<number | null>(null);
  const [priorityValue, setPriorityValue] = useState<number | null>(null);
  const [progressValue, setProgressValue] = useState<number | null>(null);

  const priorityField = usePriorityField();
  const effortField = useEffortField();

  const createdBy = useGetUserByUserId(task.createdBy);

  const createdOn = useMemo(() => {
    const now = moment();
    const createdOnMoment = moment(task.createdAt);

    if (now.isSame(createdOnMoment, 'year')) {
      return createdOnMoment.format('DD MMM');
    }

    return createdOnMoment.format('DD MMM YYYY');
  }, [task.createdAt]);

  const startedOn = useMemo(() => {
    const now = moment();
    const startedOnMoment = moment(task.startDateActual);

    if (now.isSame(startedOnMoment, 'year')) {
      return startedOnMoment.format('DD MMM');
    }

    return startedOnMoment.format('DD MMM YYYY');
  }, [task.startDateActual]);

  useEffect(() => {
    setEffortValue(task?.effort ?? null);
  }, [task?.effort]);

  useEffect(() => {
    setPriorityValue(
      (task?.fields?.find(
        (f) => f.fieldName?.toLowerCase() === PRIORITY_FIELD_NAME.toLowerCase(),
      )?.fieldValue as number) ?? null,
    );
  }, [priorityField?.fieldId, task?.fields]);

  useEffect(() => {
    setProgressValue(task?.progress ?? null);
  }, [task?.progress]);

  const updateProgress = useCallback(
    (val: number | null) => {
      TaskAPI.updateTask(task.project.projectId, task.taskId, {
        progress: val,
      }).then(() => {
        if (refetchTask) {
          refetchTask();
        }
      });
    },
    [refetchTask, task.project.projectId, task.taskId],
  );

  return (
    <CpTDFieldSectionWrapper>
      <CpTDFieldSectionTitle>
        <FormattedMessage id="default" />
      </CpTDFieldSectionTitle>
      <CpTDFieldsWrapper>
        {createdBy ? (
          <CpTaskDrawerReadonlyField
            label={<FormattedMessage id="created_by" />}
            value={
              <PaddingLeft>
                <CreatedBy user={createdBy} />
              </PaddingLeft>
            }
          />
        ) : (
          <></>
        )}

        <CpTaskDrawerReadonlyField
          label={<FormattedMessage id="created_on" />}
          value={<PaddingLeft>{createdOn}</PaddingLeft>}
        />

        {task.startDateActual && (
          <CpTaskDrawerReadonlyField
            label={<FormattedMessage id="start_date_Actual" />}
            value={<PaddingLeft>{startedOn}</PaddingLeft>}
          />
        )}

        {effortField ? (
          <CpTaskDrawerReadonlyField
            value={
              <StyledEffortDurationField
                onChange={(change) => {
                  setEffortValue(change);
                  TaskAPI.updateTask(task.project.projectId, task.taskId, {
                    effort: change,
                  }).then(() => {
                    if (refetchTask) {
                      refetchTask();
                    }
                  });
                }}
                field={effortField as unknown as NumberField}
                value={effortValue ?? 0}
              />
            }
            label={<FormattedMessage id="effort" />}
          />
        ) : (
          <></>
        )}

        <CpTaskDrawerReadonlyField
          value={
            <ProgressBar
              className="progress-bar-input"
              value={progressValue ?? 0}
              onChange={(value) => {
                setProgressValue(value);
              }}
              onAfterChange={(value) => {
                updateProgress(value);
              }}
              onInputPressEnter={(e) => e.currentTarget.blur()}
              onInputBlur={() => updateProgress(progressValue)}
            />
          }
          label={<FormattedMessage id="progress" />}
        />

        {priorityField ? (
          <CpTaskDrawerReadonlyField
            value={
              <FullWidthSpan>
                <CpTdSingleSelectField
                  field={{ ...priorityField, value: priorityValue }}
                  onChange={(value) => {
                    setPriorityValue(value as number);
                    TaskAPI.updateTask(task.project.projectId, task.taskId, {
                      priority: value,
                      fields: [
                        {
                          fieldId: priorityField.fieldId,
                          fieldValue: value,
                        },
                      ],
                    }).then(() => {
                      if (refetchTask) {
                        refetchTask();
                      }
                    });
                  }}
                />
              </FullWidthSpan>
            }
            label={<FormattedMessage id="priority" />}
          />
        ) : (
          <></>
        )}
      </CpTDFieldsWrapper>
    </CpTDFieldSectionWrapper>
  );
};
